import { gql, useQuery } from "@apollo/client";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CLIENT_VERSION } from "../constants/client-version";
import {
  CompatibilityError,
  subscribeCompatibilityError,
} from "../version-events";

const APP_CONFIG_QUERY = gql`
  query AppConfig {
    appConfig {
      latestVersion
      minimumSupportedVersion
      forceUpdate
      forceUpdateMessage
      updateUrl
      maintenanceMode
      maintenanceMessage
      enabledFeatures
      enabledGraphqlFields
    }
  }
`;

export interface AppConfig {
  latestVersion: string;
  minimumSupportedVersion: string;
  forceUpdate: boolean;
  forceUpdateMessage?: string | null;
  updateUrl?: string | null;
  maintenanceMode: boolean;
  maintenanceMessage?: string | null;
  enabledFeatures: string[];
  enabledGraphqlFields: string[];
}

interface VersionContextValue {
  config: AppConfig | null;
  hasFeature: (key: string) => boolean;
  supportsField: (fieldPath: string) => boolean;
  refresh: () => Promise<unknown>;
}

const VersionContext = createContext<VersionContextValue | null>(null);

function CompatibilityScreen({
  title,
  message,
  actionLabel,
  onAction,
  secondary,
}: {
  title: string;
  message: string;
  actionLabel: string;
  onAction: () => void;
  secondary?: string;
}) {
  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#f7f7fb",
        padding: 24,
      }}
    >
      <section
        style={{
          width: "min(460px, 100%)",
          borderRadius: 20,
          background: "white",
          boxShadow: "0 18px 60px rgba(37, 21, 125, 0.12)",
          padding: 32,
          textAlign: "center",
        }}
      >
        <h1 style={{ margin: "0 0 12px", color: "#25157d" }}>{title}</h1>
        <p style={{ color: "#4b5563", lineHeight: 1.6 }}>{message}</p>
        {secondary && (
          <p style={{ color: "#6b7280", fontSize: 14 }}>{secondary}</p>
        )}
        <button
          type="button"
          onClick={onAction}
          style={{
            marginTop: 12,
            border: 0,
            borderRadius: 10,
            background: "#25157d",
            color: "white",
            cursor: "pointer",
            fontWeight: 600,
            padding: "12px 22px",
          }}
        >
          {actionLabel}
        </button>
      </section>
    </main>
  );
}

export function VersionProvider({ children }: { children: ReactNode }) {
  const [runtimeError, setRuntimeError] =
    useState<CompatibilityError | null>(null);
  const { data, error, refetch } = useQuery<{
    appConfig: AppConfig;
  }>(APP_CONFIG_QUERY, {
    fetchPolicy: "network-only",
    pollInterval: 60_000,
    notifyOnNetworkStatusChange: true,
  });

  const config = data?.appConfig ?? null;
  const featureSet = useMemo(
    () => new Set(config?.enabledFeatures ?? []),
    [config?.enabledFeatures],
  );
  const fieldSet = useMemo(
    () => new Set(config?.enabledGraphqlFields ?? []),
    [config?.enabledGraphqlFields],
  );
  const hasFeature = useCallback((key: string) => featureSet.has(key), [featureSet]);
  const supportsField = useCallback(
    (fieldPath: string) => fieldSet.has(fieldPath),
    [fieldSet],
  );
  const refresh = useCallback(async () => {
    const result = await refetch();
    const next = result.data?.appConfig;
    if (next && !next.forceUpdate && !next.maintenanceMode) {
      setRuntimeError(null);
    }
    return result;
  }, [refetch]);
  useEffect(
    () => subscribeCompatibilityError(setRuntimeError),
    [],
  );
  const value = useMemo<VersionContextValue>(
    () => ({ config, hasFeature, supportsField, refresh }),
    [config, hasFeature, supportsField, refresh],
  );

  // if (loading && !config) {
  //   return (
  //     <CompatibilityScreen
  //       title="Loading DentMent"
  //       message="Checking application compatibility…"
  //       actionLabel="Retry"
  //       onAction={() => void refresh()}
  //     />
  //   );
  // }

  if (error && !config) {
    return (
      <CompatibilityScreen
        title="Unable to connect"
        message="DentMent could not check the server configuration. Check your connection and try again."
        actionLabel="Retry"
        onAction={() => void refresh()}
      />
    );
  }

  if (runtimeError?.code === "MAINTENANCE" || config?.maintenanceMode) {
    return (
      <CompatibilityScreen
        title="Maintenance in progress"
        message={
          runtimeError?.message ??
          config?.maintenanceMessage ??
          "DentMent is temporarily unavailable while we perform maintenance."
        }
        actionLabel="Check again"
        onAction={() => void refresh()}
      />
    );
  }

  if (runtimeError?.code === "FORCE_UPDATE" || config?.forceUpdate) {
    return (
      <CompatibilityScreen
        title="Update required"
        message={
          runtimeError?.message ??
          config?.forceUpdateMessage ??
          "This version of DentMent is no longer supported. Please update to continue."
        }
        secondary={`Installed ${CLIENT_VERSION} · Latest ${config?.latestVersion ?? "unknown"}`}
        actionLabel="Update now"
        onAction={() => {
          const updateUrl = runtimeError?.updateUrl ?? config?.updateUrl;
          if (updateUrl) {
            window.location.assign(updateUrl);
          } else {
            window.location.reload();
          }
        }}
      />
    );
  }

  return (
    <VersionContext.Provider value={value}>{children}</VersionContext.Provider>
  );
}

export function useVersion(): VersionContextValue {
  const value = useContext(VersionContext);
  if (!value) {
    throw new Error("useVersion must be used inside VersionProvider");
  }
  return value;
}
